'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Services', href: '/services-overview' },
      { label: 'Industries', href: '/industry-solutions' },
      { label: 'Contact', href: '/contact' },
    ],
    support: [
      { label: 'Client Portal', href: '/client-portal' },
      { label: '24/7 Support', href: '/contact' },
      { label: 'Knowledge Base', href: '/contact' },
      { label: 'System Status', href: '/contact' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/contact' },
      { label: 'Terms of Service', href: '/contact' },
      { label: 'Security', href: '/contact' },
      { label: 'Compliance', href: '/contact' },
    ],
  };

  const socialLinks = [
    { icon: 'LinkIcon', label: 'LinkedIn', href: '#' },
    { icon: 'LinkIcon', label: 'Twitter', href: '#' },
    { icon: 'LinkIcon', label: 'Facebook', href: '#' },
    { icon: 'LinkIcon', label: 'YouTube', href: '#' },
  ];

  return (
    <footer className="bg-primary text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-4 group">
              <div className="w-10 h-10 bg-gradient-to-br from-accent to-brand-secondary rounded-lg flex items-center justify-center">
                <span className="text-white font-headline font-bold text-xl">TS</span>
              </div>
              <div className="flex flex-col">
                <span className="font-headline font-bold text-lg text-white leading-tight">
                  TechSolutions
                </span>
                <span className="font-headline font-semibold text-xs text-accent leading-tight">
                  Pro
                </span>
              </div>
            </Link>
            
            <p className="font-body text-white/80 leading-relaxed mb-6 max-w-sm">
              Your trusted technology partner for innovative IT solutions, cybersecurity, and digital transformation services.
            </p>
            
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-2 h-2 bg-error rounded-full animate-pulse-slow" />
              <span className="font-cta font-semibold text-sm text-error">24/7 Emergency Support</span>
            </div>
            
            <div className="flex items-center space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 hover:bg-accent rounded-lg flex items-center justify-center transition-colors duration-200"
                  aria-label={social.label}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>
          
          {/* Company Links */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-white/80 hover:text-accent transition-colors duration-200 flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Support Links */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-white/80 hover:text-accent transition-colors duration-200 flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Legal Links */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="font-body text-white/80 hover:text-accent transition-colors duration-200 flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="MapPinIcon" size={20} variant="outline" className="text-accent mt-1" />
            <div>
              <p className="text-sm font-cta font-semibold mb-1">Visit Us</p>
              <p className="text-sm text-white/80 font-body">Office 14 baldiyah street, Aziziya, Jeddah, Saudi Arabia</p>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-sm text-white/60 text-center md:text-left">
              {isHydrated ? `© ${currentYear}` : '© 2026'} Centronics IT Soloutions. All rights reserved.
            </p>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={16} className="text-success" variant="solid" />
                <span className="font-body text-xs text-white/60">SOC 2 Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="LockClosedIcon" size={16} className="text-success" variant="solid" />
                <span className="font-body text-xs text-white/60">ISO 27001</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckBadgeIcon" size={16} className="text-success" variant="solid" />
                <span className="font-body text-xs text-white/60">HIPAA Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;